// Round planning API service functions
import { API_BASE_URL as BASE_URL } from '../config';

const API_BASE_URL = `${BASE_URL}/rondtes`;

// Helper function to get authorization headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

// Send a request and use the server's error message (e.g. "Sluit eers Rondte 1") when there is one
const request = async (path, fallback, options = {}) => {
  const response = await fetch(`${API_BASE_URL}${path}`, { headers: getAuthHeaders(), ...options });
  if (!response.ok) {
    let error = fallback;
    try {
      error = (await response.json()).error || fallback;
    } catch {
      // not JSON - keep the general message
    }
    throw new Error(error);
  }
  return response.json();
};

/** Admin: the whole plan - rounds with sessions, teams, stored ranking and counts */
export const getPlan = () => request('', 'Kon nie die rondtes laai nie');

/** Admin: save the plan (rounds with name, number of teams, beoordelaars per team and session times) */
export const savePlan = (rondtes) =>
  request('', 'Kon nie die rondtes stoor nie', { method: 'PUT', body: JSON.stringify({ rondtes }) });

/** Admin: start a round, with the previous round's ranking and how many teams go through (null = all) */
export const beginRondte = (rondteId, { ranglys, aantal } = {}) =>
  request(`/${rondteId}/begin`, 'Kon nie die rondte begin nie', { method: 'POST', body: JSON.stringify({ ranglys, aantal }) });

/** Admin: close a round (beoordelaars can then only read their punte) */
export const sluitRondte = (rondteId) =>
  request(`/${rondteId}/sluit`, 'Kon nie die rondte sluit nie', { method: 'POST' });

/** Admin: reopen a closed round */
export const heropenRondte = (rondteId) =>
  request(`/${rondteId}/heropen`, 'Kon nie die rondte heropen nie', { method: 'POST' });

/** Admin: allocate the beoordelaars of a round again (before any punte are given) */
export const herallokeer = (rondteId) =>
  request(`/${rondteId}/herallokeer`, 'Kon nie die beoordelaars weer toeken nie', { method: 'POST' });

/** Admin: the ranking since the last cut ({ berekende, gestoor }) */
export const getRanglys = (rondteId) => request(`/${rondteId}/ranglys`, 'Kon nie die ranglys laai nie');

/** Admin: confirm the ranking of a closed round (e.g. the final result) */
export const bevestigUitslag = (rondteId, ranglys) =>
  request(`/${rondteId}/uitslag`, 'Kon nie die uitslag stoor nie', { method: 'POST', body: JSON.stringify({ ranglys }) });

/** Admin: every allocation of a round, per session */
export const getToekennings = (rondteId) => request(`/${rondteId}/toekennings`, 'Kon nie die toekennings laai nie');

/** Admin: change one beoordelaar's team in one session of an open round (spanId null = no team) */
export const stelToekenning = (rondteId, { sessieId, beoordelaar, spanId }) =>
  request(`/${rondteId}/toekennings`, 'Kon nie die toekenning verander nie', {
    method: 'PUT',
    body: JSON.stringify({ sessie_id: sessieId, beoordelaar, span_id: spanId })
  });

/** Admin: the overall standings over every started round ({ rondtes, spanne }) */
export const getStand = () => request('/stand', 'Kon nie die algehele stand laai nie');

/** Admin: clear everything except the admin users and the default kriteria (bevestig must be 'SKOONMAAK') */
export const maakSkoon = (bevestig) =>
  request('/skoonmaak', 'Kon nie die databasis skoonmaak nie', { method: 'POST', body: JSON.stringify({ bevestig }) });

/** Admin, test data: create realistic teams with members (existing names are skipped) */
export const maakToetsSpanne = (aantal) =>
  request('/toets/spanne', 'Kon nie die toetsspanne skep nie', { method: 'POST', body: JSON.stringify({ aantal }) });

/** Admin, test data: create realistic beoordelaars (username = first name, password = username) */
export const maakToetsBeoordelaars = (aantal) =>
  request('/toets/beoordelaars', 'Kon nie die toetsbeoordelaars skep nie', { method: 'POST', body: JSON.stringify({ aantal }) });

/** Admin, test data: every beoordelaar of an open round marks their allocated teams */
export const simuleerPunte = (rondteId) =>
  request(`/${rondteId}/simuleer`, 'Kon nie die punte simuleer nie', { method: 'POST' });

/** Anyone: a light list of the rounds, for the round selectors */
export const getRondtesLys = () => request('/lys', 'Kon nie die rondtes laai nie');

/** Beoordelaar: the current round with their team (and time) per session */
export const getSkedule = () => request('/huidige/skedule', 'Kon nie jou skedule laai nie');

/** Beoordelaar: a specific round with their team (and time) per session */
export const getSkeduleVirRondte = (rondteId) => request(`/${rondteId}/skedule`, 'Kon nie jou skedule laai nie');
